export type PageDataOriginKind =
  | "manual"
  | "upstream"
  | "workflow"
  | "calculated"
  | "configuration"
  | "mixed";

export interface PageDataOrigin {
  title: string;
  kind: PageDataOriginKind;
  source: string;
  action: string;
  noCreateReason: string;
}

export const pageDataOriginKindLabels: Record<PageDataOriginKind, string> = {
  manual: "手工录入",
  upstream: "上游单据生成",
  workflow: "状态流转生成",
  calculated: "系统计算",
  configuration: "配置维护",
  mixed: "手工与系统共同生成",
};

const origin = (
  title: string,
  kind: PageDataOriginKind,
  source: string,
  action: string,
  noCreateReason: string,
): PageDataOrigin => ({ title, kind, source, action, noCreateReason });

const workflowList = (title: string) =>
  origin(
    title,
    "workflow",
    "销售、采购等业务单据提交审批后，系统按照已发布的审批模板和直属主管关系自动建立审批实例，并按经办人身份归入对应列表。",
    "先在来源业务单据执行提交审核；审批人随后在“我的待办”处理。",
    "审批记录必须保留来源单据、流程版本和处理人，不能在列表中脱离业务单据直接新增。",
  );

const viewOrigins: Record<string, Record<string, PageDataOrigin>> = {
  "/analytics": {
    overview: origin("经营总览", "calculated", "汇总销售、采购、库存、生产、往来、资金和预警等现有业务数据。", "先完成日常业务单据，再刷新总览查看最新指标。", "总览是经营结果，直接新增一行会破坏统计口径。"),
    alerts: origin("预警处理", "calculated", "系统按预警规则检查当前逾期、缺货、异常和待处理业务，生成或更新预警记录。", "点击“检查当前业务”，处理来源业务后再次检查并确认解决。", "预警必须由真实业务条件触发，不能手工伪造。"),
    rules: origin("预警规则", "configuration", "系统预置规则目录，管理员维护启用状态、阈值和负责人。", "使用“配置”修改规则，保存后重新检查当前业务。", "规则代码属于系统能力目录，只允许配置现有规则。"),
  },
  "/workflow": {
    TODO: workflowList("我的待办"),
    DONE: workflowList("我已处理"),
    MINE: workflowList("我的申请"),
    COPIED: workflowList("抄送给我"),
    ALL: workflowList("全部申请"),
    templates: origin("审批模板", "configuration", "管理员按业务类型新建审批模板，配置审批节点后发布。", "点击“新增模板”，保存草稿并发布；后续提交的业务使用发布版本。", "这里可以新增模板，但不能在此新增审批实例。"),
    managers: origin("直属主管", "configuration", "管理员维护员工与直属主管关系，供按主管审批的流程节点取人。", "点击“设置直属主管”维护关系。", "一名员工同一时点只有一条生效主管关系，使用设置/修改而不是重复新增。"),
  },
  "/equipment": {
    tasks: origin("点检保养任务", "upstream", "从已启用的周期计划按日期范围批量生成，每个计划每天只生成一次。", "先维护设备和周期计划，再点击“生成点检保养任务”，执行人填写检查结果。", "任务必须引用设备、计划快照和计划日期，因此不提供空白任务新增。"),
    plans: origin("周期计划", "manual", "设备管理员为设备手工建立点检或保养周期、首次日期、执行人和检查项目。", "点击“新增周期计划”，启用后再生成任务。", "支持新增；没有权限时只展示现有计划。"),
    assets: origin("设备档案", "manual", "设备管理员录入设备编码、名称、位置、责任人和状态。", "点击“新增设备”，保存后可建立周期计划或发起报修。", "支持新增；设备编码必须唯一。"),
    faults: origin("故障与维修", "mixed", "设备使用人可针对设备报修；维修、验收和复机动作继续更新同一故障单。", "点击“故障报修”，随后按接单、维修、验收、复机顺序处理。", "可新增报修，但维修记录必须在原故障单上流转，不能另造无来源记录。"),
    analysis: origin("设备指标", "calculated", "从设备档案、点检任务、故障停机和维修结果计算完好率、停机时长等指标。", "维护并执行设备业务后刷新查看。", "指标由明细计算，不能手工新增。"),
  },
  "/quality": {
    inspections: origin("检验任务", "upstream", "已发布检验标准会在采购入库或生产工序完成时自动生成任务；也可从符合条件的既有来源补建。", "先发布检验标准，再执行采购入库或工序报工；需要补录时点击“建立检验任务”选择实际来源。", "不能创建没有来源明细和标准快照的空白检验。"),
    standards: origin("检验标准", "manual", "质量人员按商品和来料、过程、成品范围建立版本化标准。", "点击“新增标准”或复制旧版，保存、评审并发布。", "支持新增；生效标准通过新版本变更。"),
    nonconformances: origin("不合格处置", "workflow", "检验判定不合格时从检验任务自动形成处置单，随后执行返工、报废、退货或让步等处置。", "先完成检验并判定不合格，再进入本页处理和复核。", "不合格单必须追溯检验记录和来源批次，不能手工新增。"),
    analysis: origin("质量分析", "calculated", "按检验任务、不合格原因和处置结果汇总一次合格率、缺陷分布等指标。", "完成检验与处置后刷新查看。", "分析数据由质量明细计算，不能新增。"),
  },
  "/planning": {
    forecasts: origin("销售预测", "manual", "计划员按期间、商品和预测数量建立需求版本。", "点击“新增预测版本”，校验后冻结版本。", "本页应提供新增；冻结后通过新版本调整，不直接改历史。"),
    mps: origin("主生产计划 MPS", "calculated", "根据冻结销售预测和已审核未交付销售订单，按所选需求口径计算成品主计划。", "先冻结预测并审核订单，再点击“生成主生产计划”。", "MPS 行是需求计算结果，不能逐行随意新增。"),
    mrp: origin("物料需求 MRP", "calculated", "运行 MPS 后，系统结合生效 BOM、库存、在途采购、在制数量和计划参数展开物料缺口。", "选择 MPS 运行 MRP，再把采购建议或生产建议转换成对应订单。", "MRP 建议必须能追溯需求和 BOM，不能手工新增结果行。"),
    capacity: origin("能力排产", "calculated", "根据已下达生产工单、工艺路线、工作中心能力和工作日历形成工序负荷。", "选择工单后自动或手工排程；调整的是排程结果，不是新增无来源负荷。", "负荷必须来自工单工序，不能单独新增。"),
    policies: origin("计划参数", "configuration", "按商品维护提前期、最小批量、订货倍数等参数；安全库存来自商品档案。", "搜索商品后维护计划参数并保存。", "每个商品只有一套当前参数，属于维护配置。"),
  },
  "/manufacturing": {
    boms: origin("BOM 版本", "manual", "工程人员为成品建立物料清单版本和用量。", "点击“新增 BOM”或复制版本，评审后发布。", "支持新增；历史工单保留建立时的 BOM 快照。"),
    routings: origin("工艺路线", "manual", "工程人员为产品建立工序、工作中心、工时和顺序的版本。", "点击“新增工艺路线”或复制版本，评审后发布。", "支持新增；变更通过新版本发布。"),
    centers: origin("工作中心", "manual", "管理员维护生产资源、能力、班次和成本参数。", "点击“新增工作中心”维护资源。", "支持新增；已有业务引用后应停用而非删除。"),
    calendar: origin("工作日历", "configuration", "按工作中心和日期生成日历，再维护可用分钟、停机和备注。", "先选择工作中心和日期范围生成日历，随后调整例外日期。", "日历按资源和日期唯一，使用批量生成与调整而不是逐条重复新增。"),
  },
  "/sales": {
    orders: origin("销售订单", "manual", "销售人员录入客户、交期、商品、数量、价格和税率，保存草稿后提交审核。", "点击“新建销售订单”；审核通过后才能出库。", "可新建销售订单，保存草稿后提交审核。"),
    executions: origin("销售出库", "upstream", "仓库针对已审核且仍有待发数量的销售订单执行分批出库。", "回到销售订单选择“出库”，填写仓库、批次、库位和本次数量。", "出库必须引用已审核订单及订单行，不能在台账中空白新增。"),
    returns: origin("销售退货", "upstream", "针对已审核销售出库的可退数量创建退货，并回写库存、往来和凭证。", "从销售出库或订单详情发起退货，选择原出库明细。", "退货必须受原出库数量约束，不能无来源新增。"),
  },
  "/purchase": {
    orders: origin("采购订单", "mixed", "采购员可手工创建，也可把 MRP 采购建议转换为采购订单。", "点击“新建采购订单”，或在 MRP 中转换采购建议；审核后才能入库。", "支持新增；来源于 MRP 时会保留计划关系。"),
    executions: origin("采购入库", "upstream", "仓库针对已审核且仍有待收数量的采购订单分批入库。", "回到采购订单选择“入库”，填写仓库、批次、库位和实收数量。", "入库必须引用已审核采购订单和订单行。"),
    returns: origin("采购退货", "upstream", "针对已审核采购入库的可退数量创建退货。", "从采购入库或订单详情发起退货，选择原入库明细。", "退货必须能追溯供应商、批次和原入库，不能空白新增。"),
    expenses: origin("采购费用", "manual", "采购或财务人员登记运费、装卸费等采购附加费用，并关联供应商、入库和费用项目。", "点击“新增采购费用”，审核后按当前规则进入存货成本或费用及应付。", "支持新增；费用项目需先在辅助资料维护。"),
  },
  "/inventory": {
    "other-inbounds": origin("其它入库单", "manual", "登记非采购、非生产来源的入库，按录入成本过账。", "点击新建其它入库单并填写业务原因。", "生产产出的成品应从成品入库办理，以便结转材料成本。"),
    "other-outbounds": origin("其它出库单", "manual", "登记非销售、非生产用途的领用或损耗。", "点击新建其它出库单并填写业务原因。", "生产用料应从生产领料办理，以便归集材料成本。"),
    "production-issues": origin("生产领料", "manual", "手工选成品和实际材料，系统建立简易生产批次，扣减材料库存并归集成本。", "点击新建生产领料单，领料后到成品入库选择该批次。", "无需 BOM 或工艺；成品入库时不会重复扣料。"),
    "production-receipts": origin("成品入库", "mixed", "已领料批次按实际成本分批入库；也可将材料耗用和成品入库一次录入。", "选择已领料批次入库，或选择材料耗用与入库一次录入。", "首次入库后材料成本冻结，末批结清尾差，需确认成品检验合格。"),
    balances: origin("库存余额", "calculated", "由期初库存、采购入库、销售出库、退货、生产领退料、完工入库、调拨和盘点过账累计得到。", "到对应业务单据执行收发、调拨或盘点；本页负责查余额与追溯来源。", "库存余额是库存账结果，直接新增会绕过单据、成本和审计。"),
    locations: origin("库位库存", "calculated", "所有带库位的上架、拣货、移位和收发动作同步更新库位数量账。", "先维护仓库库位，再在入库、出库和移位时选择库位。", "库位余额是动作结果，不能手工新增数量。"),
    logs: origin("出入库流水", "workflow", "每次已确认的入库、出库、退货、领退料、完工、调拨和盘点过账自动写入流水。", "在来源业务完成库存动作后，到本页按单号、商品或仓库查询。", "流水是不可随意篡改的审计记录，不允许新增或编辑。"),
    transfers: origin("库存调拨", "manual", "仓库人员创建调拨单，选择调出仓、调入仓和商品批次，随后发出并接收。", "点击“库存调拨”，按发出、在途、接收流程处理。", "支持新增；库存变化发生在确认动作。"),
    checks: origin("库存盘点", "manual", "仓库人员按仓库及商品范围生成盘点任务，系统锁定账面快照；录入实盘后由另一授权人员复核过账。", "点击“生成盘点任务”，录入实盘、提交复核并过账差异。", "使用生成任务而非空白新增，以确保账面快照和职责分离。"),
  },
  "/settlement": {
    "open-items": origin("往来余额", "workflow", "销售出库、采购入库、退货、采购费用、期初和核销动作自动形成或冲减应收应付往来项。", "从来源业务形成往来，再用收款、付款、对冲或预收预付核销结清。", "往来项属于业务账，不允许脱离来源直接新增。"),
    receipts: origin("收款单", "manual", "财务人员登记客户收款，可选择应收项核销；未分配金额形成预收。", "点击“新增收款单”，保存并由授权人员审核。", "支持新增；审核时才更新资金、往来和凭证。"),
    payments: origin("付款单", "manual", "财务人员登记供应商付款，可选择应付项核销；未分配金额形成预付。", "点击“新增付款单”，保存并由授权人员审核。", "支持新增；审核时才更新资金、往来和凭证。"),
    aging: origin("账龄分析", "calculated", "按尚未结清的往来项、业务日期和到期日分段计算账龄。", "先形成并核销往来，再选择基准日期查看。", "账龄是计算视图，不能新增。"),
    accounts: origin("结算账户", "configuration", "财务人员维护现金、银行等收付款账户及启用状态。", "点击“新增结算账户”维护账号资料。", "支持新增；账户余额由审核后的收付款产生。"),
    offsets: origin("退货对冲", "upstream", "把退货形成的反向往来与原销售或采购往来匹配对冲。", "先完成原业务和退货，再在本页选择可匹配的正反往来确认对冲。", "只能选择现有可对冲项，不能新增虚构余额。"),
    applications: origin("预收预付核销", "upstream", "把未分配收付款形成的预收预付，应用到后来产生的应收应付。", "先审核未完全分配的收付款并形成新往来，再在本页选择双方核销。", "核销关系来自已有余额，不能空白新增。"),
    reconciliations: origin("往来对账单", "calculated", "按单位和期间汇总期初、发生、核销和期末余额，生成可确认的对账快照。", "选择客户或供应商及期间生成对账单，再确认结果。", "明细来自往来账；新增的是对账快照，不是手工录入账务行。"),
    invoices: origin("发票管理", "upstream", "从已确认的销售出库、采购入库及退货中选择可开票金额，登记蓝字或红字发票并确认匹配。", "点击登记发票并选择来源单据；确认后更新票据匹配状态。", "发票必须受来源可开票金额约束；这里是发票登记，不是税务平台实际开具。"),
  },
  "/finance": {
    vouchers: origin("记账凭证", "mixed", "收发货、收付款、退货、盘点、成本等业务审核后自动生成业务凭证；财务也可新增手工凭证。", "自动凭证从来源业务查看；调整类事项点击“新增凭证”，审核后记账。", "本页有手工新增，但已记账业务应通过冲销而非直接修改。"),
    subjects: origin("会计科目", "configuration", "账套初始化时建立科目表，财务可在未被限制时维护明细科目和状态。", "使用新增/编辑维护科目，已有发生额的科目通常停用而不删除。", "本页属于配置；系统控制科目编码唯一和引用约束。"),
    trial: origin("试算平衡", "calculated", "按期间汇总已记账凭证的期初、借贷发生和期末余额，并检查借贷平衡。", "先审核并记账凭证，再选择期间查询。", "试算表是账簿计算结果，不能新增。"),
    auxiliary: origin("辅助核算账", "calculated", "从已记账凭证明细中的客户、供应商、部门、员工等辅助维度汇总。", "在科目和业务映射中启用辅助核算，记账后查询。", "辅助账来自凭证行，不能独立新增。"),
    general: origin("总账", "calculated", "从已记账凭证按科目和期间形成期初、发生额、方向和余额。", "选择科目与期间查询，并可追溯凭证。", "总账是法定账簿结果，不能直接新增或改余额。"),
    periods: origin("会计期间", "configuration", "按会计年度生成月份期间，业务记账和结账状态控制可用范围。", "生成会计年度并按月开放、结账或按权限反结账。", "期间按年度和月份唯一，不逐条重复新增。"),
    mappings: origin("业务科目映射", "configuration", "财务为销售、采购、库存、资金等业务事件配置借贷科目。", "逐项维护映射并保存，再用已审核业务单据核对自动凭证。", "事件目录由系统定义，只维护科目映射。"),
    costs: origin("库存成本", "calculated", "系统根据期初金额、入库成本和出库计价计算库存成本，并在成本重算后形成差异记录。", "完成库存业务后执行成本核对或重算，再检查差异及凭证。", "成本余额必须由库存流水和计价规则计算，不能手工新增。"),
    statements: origin("财务报表", "calculated", "资产负债表、利润表和现金流量相关数据从已记账凭证、科目属性和报表口径汇总。", "完成记账后选择期间生成、核对并按需保存快照或导出。", "报表行是计算结果，不能新增业务金额。"),
    opening: origin("期初余额", "manual", "系统启用前由财务录入科目期初、辅助期初和库存期初，经平衡校验后确认。", "录入期初，在发生正式业务前校验并确认。", "可录入期初；确认且已有业务后会受锁定，不能当作日常新增。"),
    closing: origin("期末结账", "workflow", "系统对未记账凭证、库存、往来、成本和在制等项目执行月结检查，通过后生成结账记录。", "选择期间运行检查，处理失败项后执行结账；必要时按权限反结账。", "结账记录由检查和结账动作生成，不能手工新增。"),
    "account-set": origin("账套信息", "configuration", "企业开通时自动建立唯一账套，保存本位币、会计制度和启用期间等设置。", "点击编辑账套设置；启用后关键字段受业务约束。", "一个企业只有一个当前账套，所以只编辑、不新增。"),
  },
  "/master-data": {
    products: origin("商品档案", "manual", "管理员或资料员录入物料/成品编码、单位、分类、追踪方式、安全库存等。", "点击“新增商品”。", "支持新增；已有业务引用后通常停用而不删除。"),
    customers: origin("客户档案", "manual", "销售或资料员维护客户编码、名称、账期、联系人等。", "点击“新增客户”。", "支持新增；订单下拉数据来自这里。"),
    suppliers: origin("供应商档案", "manual", "采购或资料员维护供应商编码、名称、账期、联系人等。", "点击“新增供应商”。", "支持新增；采购业务下拉数据来自这里。"),
    warehouses: origin("仓库档案", "manual", "仓库管理员维护仓库编码、名称、负责人和启用状态。", "点击“新增仓库”，再维护库位。", "支持新增；已有库存的仓库不能随意删除。"),
    units: origin("计量单位", "manual", "资料员维护件、套、千克等业务单位。", "点击“新增计量单位”。", "支持新增；被商品引用后应谨慎变更。"),
    categories: origin("商品分类", "manual", "资料员维护商品分类层级。", "点击“新增分类”。", "支持新增；分类用于商品检索和统计。"),
  },
  "/organization": {
    employees: origin("员工与账号", "manual", "企业管理员建立员工登录账号、所属部门、角色和状态。", "点击新增员工；角色决定菜单和按钮权限。", "支持新增；离职账号应停用以保留历史经办记录。"),
    departments: origin("部门架构", "manual", "企业管理员维护部门层级和负责人。", "点击新增部门或子部门，再分配员工。", "支持新增；已有员工的部门不能直接删除。"),
  },
};

const referenceOrigins: Record<string, PageDataOrigin> = {
  TEAM: origin("班组", "manual", "维护生产班组，供工单、排产和报工选择。", "点击新增资料并维护编码、名称和状态。", "支持新增；被引用后停用而不删除。"),
  TAX_RATE: origin("税率", "configuration", "维护销售、采购和发票使用的税率档案。", "点击新增资料并维护税率编码和值。", "支持新增；历史单据保留原税率快照。"),
  EXPENSE_ITEM: origin("费用项目", "configuration", "维护采购费用和报销类业务使用的费用项目及关联科目。", "点击新增资料并配置关联科目。", "支持新增。"),
  COST_ITEM: origin("成本项目", "configuration", "维护材料、人工、制造费用等成本分类档案；库存成本仍按出入库计价和费用分摊计算。", "点击新增资料维护分类编码、名称及关联科目。", "成本项目本身不生成成本或凭证。"),
  WAREHOUSE_LOCATION: origin("库位", "manual", "按仓库维护库位编码、名称和启用状态，供上架、移位和拣货选择。", "先建立仓库，再点击新增库位。", "支持新增；库位数量在库位库存页由业务动作产生。"),
  VOUCHER_WORD: origin("凭证字", "configuration", "维护记、收、付、转等凭证字档案；当前凭证号码由系统编号规则生成。", "点击新增资料维护编码、名称和用途说明。", "凭证字档案不会重编已有凭证。"),
};

const standaloneOrigins: Record<string, PageDataOrigin> = {
  "/workspace": origin("经营首页", "calculated", "汇总当前账号可见的经营指标、待办、快捷入口和账簿摘要。", "先完成各模块业务，再刷新首页；待办从审批、异常和任务自动汇集。", "首页是工作台，不直接新增业务数据。"),
  "/production": origin("生产工单", "mixed", "计划员可手工创建生产工单，也可将 MRP 生产建议转换为工单；下达时固化 BOM 和工艺。", "可新增工单或从 MRP 转换；简易生产从“新建生产领料单/完工入库单 → 无工单，独立录入”填写成品与实际材料，自动建立成本批次。", "支持工单执行与独立生产录入；实际领料、加工费用和成品入库联动库存、在制成本与凭证。"),
  "/organization/positions": origin("岗位与在岗员工", "manual", "管理员维护岗位目录，并把员工分配到岗位。", "点击新增岗位，再在员工维护中关联。", "支持新增；岗位和部门是不同维度。"),
  "/subscription": origin("套餐与用量", "calculated", "套餐由平台侧开通或续费；用量由企业用户、仓库、商品和存储等实际资源实时统计。", "企业侧仅查看；变更套餐由平台管理员按合同执行。", "企业端不能新增套餐或自行突破额度。"),
  "/access": origin("权限与安全", "mixed", "角色、打印模板和开放 API 由管理员维护；会话、授权结果、导出任务和审计记录由系统产生。", "在对应卡片新增角色/模板/API 客户端或发起导出；员工角色在组织管理分配。", "当前授权和审计属于系统结果，不提供手工新增。"),
  "/tasks": origin("后台任务中心", "workflow", "导出、批处理和预警检查等耗时操作提交后自动建立后台任务。", "从具体业务入口发起任务，再到本页查看进度、失败原因或重试。", "后台任务必须包含真实任务类型和参数，不能空白新增。"),
};

const defaultViews: Record<string, string> = {
  "/analytics": "overview",
  "/workflow": "TODO",
  "/equipment": "tasks",
  "/quality": "inspections",
  "/planning": "forecasts",
  "/manufacturing": "boms",
  "/sales": "orders",
  "/purchase": "orders",
  "/inventory": "balances",
  "/settlement": "open-items",
  "/finance": "vouchers",
  "/master-data": "products",
  "/organization": "employees",
};

const genericOrigin = origin(
  "当前页面",
  "mixed",
  "数据可能由手工录入、上游单据状态流转或系统计算产生。",
  "请从页面右上角操作入口或来源单据开始；结果型台账通常只提供查询和追溯。",
  "是否显示新增取决于数据是否允许脱离来源独立存在，以及当前账号权限。",
);

export function getPageDataOrigin(pathname: string, search = ""): PageDataOrigin {
  if (pathname === "/master-data/references") {
    const kind = new URLSearchParams(search).get("kind") ?? "TEAM";
    return referenceOrigins[kind] ?? genericOrigin;
  }
  if (pathname === "/organization/positions") {
    return standaloneOrigins[pathname] ?? genericOrigin;
  }
  const standalone = standaloneOrigins[pathname];
  if (standalone) return standalone;
  const views = viewOrigins[pathname];
  if (!views) return genericOrigin;
  const requested = new URLSearchParams(search).get("view");
  const defaultView = defaultViews[pathname] ?? "";
  return views[requested ?? defaultView] ?? views[defaultView] ?? genericOrigin;
}
